import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IDoctorAgenda } from "../../models/doctor-agenda";
import { useDoctorAgenda } from "./DoctorAgenda/useDoctorAgenda";
import ScheduleTypeIcon from "./ScheduleTypeIcon";

const translation_path = "page.doctorAgenda.";

const FREE_CALENDAR_STATUS_CODE = "L";

interface AppointmentCardProps {
  appointment: IDoctorAgenda;
  icon: ReactNode;
  hour: string;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  icon,
  hour,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const isFree = appointment.calendarStatusCode === FREE_CALENDAR_STATUS_CODE;

  const { getBrazilianDateISO, dayjsBrazil } = useDoctorAgenda();
  const handleCardClick = useCallback(() => {
    const date = dayjsBrazil(appointment.calendarStartDate);
    navigate(
      `/doctor-agenda/appointmentDetails/${getBrazilianDateISO(
        date.toDate()
      )}/${appointment.identifier}`
    );
  }, [appointment, navigate, dayjsBrazil, getBrazilianDateISO]);

  return (
    <Card
      variant="outlined"
      onClick={isFree ? undefined : handleCardClick}
      sx={{
        cursor: isFree ? "default" : "pointer",
        "&:hover": {
          backgroundColor: isFree ? undefined : "action.hover",
        },
      }}
    >
      <CardContent
        sx={{
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack alignItems={"center"} spacing={0.5}>
              <ScheduleTypeIcon
                tasyScheduleTypeCode={appointment.tasyScheduleTypeCode}
              />
              {icon}
            </Stack>

            <Stack spacing={1}>
              {appointment.tasyScheduleTypeCode === 3 && (
                <Typography variant="body2">
                  {appointment.calendarClassificationName}
                </Typography>
              )}
              {(appointment.tasyScheduleTypeCode === 2 ||
                appointment.tasyScheduleTypeCode === 1) && (
                <Typography variant="body2">
                  {appointment.calendarClassificationName &&
                    appointment.calendarClassificationName}
                  {appointment.calendarClassificationName &&
                    appointment.procedureName &&
                    " - "}
                  {appointment.procedureName}
                </Typography>
              )}
              {isFree ? (
                <Box>
                  <Chip
                    label={t(translation_path + "free_time")}
                    size="small"
                    sx={{
                      backgroundColor: "#B9F6CA",
                      color: "#1B5E20",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body1" fontWeight="bold">
                  {appointment.pacientName}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {hour}{" "}
                {appointment.calendarShortName
                  ? " - " + appointment.calendarShortName
                  : appointment.descriptionSite
                  ? " - " + appointment.descriptionSite
                  : ""}
              </Typography>
            </Stack>
          </Stack>

          {!isFree && (
            <ChevronRight sx={{ fontSize: 30, color: "action.active" }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AppointmentCard;
